import { useEffect, useMemo, useState, useLayoutEffect } from "react";
import { Routes, Route, NavLink, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar,
  MapPin,
  Mail,
  Gift,
  Heart,
  Clock,
  Info,
  Bird,
  Bug,
  Flower2,
  Leaf,
  Apple,
  Menu,
  X,
} from "lucide-react";
import confetti from "canvas-confetti";



// === CONFIG ===
const COUPLE = { bride: "Sarah", groom: "Alec" };
const WEDDING = {
  dateISO: "2026-08-13T14:00:00",
  datePretty: "Thursday, 13 August 2026",
  venueName: "Chapel House Estate",
  venueAddress: "Ramsgate, Kent, UK",
  venueMapUrl:
    "https://maps.google.com/?q=Chapel+House+Estate+Ramsgate+Kent+UK",
  rsvpEmail: "alec.lindsell@gmail.com",
  rsvpFormUrl: "https://forms.gle/yoBV89o6tfn9x3f66",
  registryUrl: "#",
  rsvpWebhookUrl: "https://script.google.com/macros/s/AKfycbwjw18aul8CMQQHSAfucb0HM6z69yWpEHxQY91aatZJWYbcSen1wpICsQKmNgfJi2mzLg/exec",

  hotels: [
  {
    name: "Premier Inn Ramsgate (Manston Airport)",
    address: "Manston Road, Minster, Ramsgate, Kent (UK)",
    note: "A reliable budget hotel option close to the venue area.",
    url: "https://www.premierinn.com/gb/en/hotels/england/kent/ramsgate/ramsgate-manston-airport.html",
  },
  {
    name: "Holiday Inn Express Ramsgate – Minster",
    address: "Tothill Street, Minster, Ramsgate, Kent (UK)",
    note: "Modern, dependable option with easy access by taxi.",
    url: "https://www.ihg.com/holidayinnexpress/hotels/gb/en/ramsgate/kenmi/hoteldetail",
  },
  {
    name: "Travelodge Ramsgate Seafront",
    address: "Royal Parade, Ramsgate, Kent (UK)",
    note: "Great value and right by the harbour/seafront.",
    url: "https://www.travelodge.co.uk/hotels/510/Ramsgate-Seafront-hotel",
  },
],
};

const GALLERY_IMAGES = [
  {
    src: "/gallery/gallery-01.webp",
    alt: "Alec and Sarah",
  },
  {
    src: "/gallery/gallery-02.webp",
    alt: "Engagement photo",
  },
  {
    src: "/gallery/gallery-03.webp", 
    alt: "Gallery photo 03", 
    orientation: "landscape",
  },
  {
    src: "/gallery/gallery-04.webp",
    alt: "Summer countryside",
    orientation: "landscape",
  },
  {
    src: "/gallery/gallery-05.webp",
    alt: "Together",
    orientation: "landscape",
  },
    {
    src: "/gallery/gallery-06.webp",
    alt: "Summer countryside",
    mobileFocus: "object-[25%_50%] sm:object-center",
  },
    {
    src: "/gallery/gallery-07.webp",
    alt: "Gallery photo 07",
  },
    {
    src: "/gallery/gallery-08.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-09.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-10.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-11.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-12.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-13.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-14.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-15.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-16.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-17.webp",
    alt: "Summer countryside",
  },
    {
    src: "/gallery/gallery-18.webp",
    alt: "Summer countryside",
  },
  {
    src: "/gallery/gallery-19.webp",
    alt: "Summer countryside",
  },
  {
    src: "/gallery/gallery-20.webp",
    alt: "Summer countryside",
  },
  {
    src: "/gallery/gallery-21.webp",
    alt: "Summer countryside",
  },
  {
    src: "/gallery/gallery-22.webp",
    alt: "Summer countryside",
    orientation: "landscape",
    fullWidth: true,
  },
];

const ADMIN = {
  // Change this to anything you like
  code: "Iceland2023",
};

const THEME = {
  // From your artwork
  sage: "#8A8554",        // Tree (olive / foliage)
  cornflower: "#ACB8C0",  // Sky Blue
  buttercup: "#C26944",   // Butterflies (warm terracotta highlight)
  lavender: "#9060BA",    // Flowers (purple)
  rose: "#B04B3D", // Apples (main CTA colour)

  // Supporting / background tones
  text: "#2B2A20",        // deep “ink” (readable on tan/cream)
  bgFrom: "#F3EEE6",      // soft “paper” background (neutral, not too yellow)
  bgTo: "#B6A286",        // House (warm tan)
};

const ACCESS = {
  password: "seaside2026",
  storageKey: "wedding_access_granted",
};

function useCountdown(targetISO) {
  const target = useMemo(() => new Date(targetISO).getTime(), [targetISO]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

function Section({ children, className = "" }) {
  return <section className={`max-w-6xl mx-auto px-6 sm:px-8 ${className}`}>{children}</section>;
}

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-3xl shadow-xl bg-white/70 backdrop-blur-md border border-white/60 ${className}`}>
      {children}
    </div>
  );
}

function AccessGate({ children }) {
  const [granted, setGranted] = useState(
    localStorage.getItem(ACCESS.storageKey) === "yes"
  );
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");

  function handleSubmit(e) {
    e.preventDefault();
if (pw.trim() === ACCESS.password) {
  localStorage.setItem(ACCESS.storageKey, "yes");
  setErr("");
  window.location.replace("/"); // forces a valid route
  return;
}


    setErr("Sorry — that password isn’t correct.");
  }

  if (granted) return children;

  // Holding / login page
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        background: `linear-gradient(to bottom, ${THEME.bgFrom}, white, ${THEME.bgTo})`,
        color: THEME.text,
      }}
    >
      <div className="w-full max-w-md rounded-3xl bg-white/80 backdrop-blur-md border border-white/60 shadow-xl p-8 text-center">
        <h1 className="font-serif text-3xl mb-2" style={{ color: THEME.cornflower }}>
          {COUPLE.groom} & {COUPLE.bride}
        </h1>

        <p className="text-sm mb-6">
          Our wedding website is for invited guests. Please enter the password from your invitation.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Enter password"
            className="w-full rounded-2xl border px-4 py-3 text-sm"
          />

          {err && <div className="text-sm" style={{ color: THEME.rose }}>{err}</div>}

          <button
            type="submit"
            className="w-full inline-flex items-center justify-center rounded-3xl px-8 py-4 text-base font-semibold text-white shadow-xl hover:opacity-95 transition"
            style={{ backgroundColor: THEME.rose }}
          >
            Enter site
          </button>
        </form>

        <p className="text-xs mt-6 opacity-70">
          Having trouble? Email us and we’ll help.
        </p>
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div className="my-4 flex justify-center">
      <img
        src="/Roses.webp"
        alt="Rose illustration"
        className="max-w-[200px] sm:max-w-[480px] opacity-90"
      />
    </div>
  );
}

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const linkClass = ({ isActive }) =>
    `hover:opacity-70 ${isActive ? "underline underline-offset-4" : ""}`;

  return (
    <div
      className="min-h-screen"
      style={{
        background: `linear-gradient(to bottom, ${THEME.bgFrom}, white, ${THEME.bgTo})`,
        color: THEME.text,
      }}
    >
      <header className="sticky top-0 z-40 backdrop-blur bg-white/70 border-b">
        <nav className="max-w-6xl mx-auto flex items-center justify-between px-6 sm:px-8 py-3">
  {/* Logo */}
  <NavLink
    to="/"
    className="font-serif text-xl sm:text-2xl tracking-wide"
    style={{ color: THEME.cornflower }}
  >
    {COUPLE.groom} & {COUPLE.bride}
  </NavLink>

  {/* Desktop navigation */}
  <div className="hidden sm:flex gap-6 text-sm">
    <NavLink to="/details" className={linkClass} style={{ color: THEME.sage }}>
      Details
    </NavLink>
    <NavLink to="/schedule" className={linkClass} style={{ color: THEME.cornflower }}>
      Schedule
    </NavLink>
    <NavLink to="/rsvp" className={linkClass} style={{ color: THEME.rose }}>
      RSVP
    </NavLink>
    <NavLink to="/travel" className={linkClass} style={{ color: THEME.lavender }}>
      Travel
    </NavLink>
    <NavLink to="/things-to-do" className={linkClass} style={{ color: THEME.buttercup }}>
      Things to do
    </NavLink>
    <NavLink to="/gallery" className={linkClass}>
      Gallery
    </NavLink>
    <NavLink to="/faq" className={linkClass}>
      FAQ
    </NavLink>
  </div>

  {/* Mobile menu button */}
  <button
  onClick={() => setMobileOpen(!mobileOpen)}
  className="md:hidden inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white/70 backdrop-blur-sm"
  style={{ borderColor: THEME.sage }}
>
  {mobileOpen ? (
    <>
      <X className="h-5 w-5" />
      <span className="text-xs tracking-wide uppercase">Close</span>
    </>
  ) : (
    <>
      <Menu className="h-5 w-5" />
      <span className="text-xs tracking-wide uppercase">Menu</span>
    </>
  )}
</button>
</nav>
{/* Mobile menu overlay */}
{mobileOpen && (
  <div className="sm:hidden absolute top-full left-0 w-full bg-white/95 backdrop-blur border-b shadow-lg z-50">
    <div className="flex items-center justify-between px-6 py-4">
      <span className="font-serif text-lg">
        {COUPLE.groom} & {COUPLE.bride}
      </span>
      <button onClick={() => setMobileOpen(false)} aria-label="Close menu">
        <X className="h-6 w-6" />
      </button>
    </div>

    <div className="flex flex-col gap-4 px-6 pb-6 text-sm">
      {[
        ["/", "Home"],
        ["/details", "Details"],
        ["/schedule", "Schedule"],
        ["/rsvp", "RSVP"],
        ["/travel", "Travel"],
        ["/things-to-do", "Things to do"],
        ["/gallery", "Gallery"],
        ["/faq", "FAQ"],
      ].map(([to, label]) => (
        <NavLink
          key={to}
          to={to}
          onClick={() => setMobileOpen(false)}
          className="py-1"
        >
          {label}
        </NavLink>
      ))}
    </div>
  </div>
)}
      </header>


<main>{children}</main>




      <footer className="mt-10 py-10 text-center text-xs text-slate-600">
        <p>
          © {new Date().getFullYear()} {COUPLE.groom} & {COUPLE.bride}. Built with ♥ for our favourite people.
        </p>
      </footer>
    </div>
  );
}

// === RSVP DEMO GUEST LIST (we'll replace with Google Sheets later) ===
// partyId groups people invited together
const RSVP_GUESTS = [
{ partyId: "P001", first: "Alec", last: "Lindsell", display: "Alec Lindsell" },
{ partyId: "P001", first: "Sarah", last: "Barry", display: "Sarah Barry" },
{ partyId: "P002", first: "Noreen", last: "Barry", display: "Noreen Barry" },
{ partyId: "P002", first: "Ger", last: "Feighery", display: "Ger Feighery" },
{ partyId: "P003", first: "Gabriella", last: "Lindsell", display: "Gabriella Lindsell" },
{ partyId: "P003", first: "Ron", last: "Lindsell", display: "Ron Lindsell" },
{ partyId: "P004", first: "John", last: "Barry", display: "John Barry" },
{ partyId: "P004", first: "Carol", last: "Barry", display: "Carol Barry" },
{ partyId: "P004", first: "Sophie", last: "Barry", display: "Sophie Barry" },
{ partyId: "P004", first: "Jack", last: "Barry", display: "Jack Barry" },
{ partyId: "P005", first: "Lucy", last: "Lindsell", display: "Lucy Lindsell" },
{ partyId: "P006", first: "Christina", last: "Lindsell", display: "Christina Lindsell" },
{ partyId: "P007", first: "Elizabeth ", last: "Adkins", display: "Elizabeth Adkins" },
{ partyId: "P007", first: "Jonathan", last: "Adkins", display: "Jonathan Adkins" },
{ partyId: "P008", first: "Bridie", last: "Mulqueen", display: "Bridie Mulqueen" },
{ partyId: "P009", first: "Denis", last: "Mulqueen", display: "Denis Mulqueen" },
{ partyId: "P009", first: "Sue", last: "Mulqueen", display: "Sue Mulqueen" },
{ partyId: "P010", first: "Bernard", last: "Mulqueen", display: "Bernard Mulqueen" },
{ partyId: "P010", first: "Evie", last: "Mulqueen", display: "Evie Mulqueen" },
{ partyId: "P011", first: "Patrick", last: "Mulqueen", display: "Patrick Mulqueen" },
{ partyId: "P011", first: "Mary", last: "Mulqueen", display: "Mary Mulqueen" },
{ partyId: "P012", first: "Bob", last: "Mulqueen", display: "Bob Mulqueen" },
{ partyId: "P012", first: "Sinead", last: "Mulqueen", display: "Sinaed Mulqueen" },
{ partyId: "P012", first: "Conor", last: "Mulqueen", display: "Conor Mulqueen" },
{ partyId: "P013", first: "Eoghan", last: "Mulqueen", display: "Eoghan Mulqueen" },
{ partyId: "P013", first: "Zoe", last: "Phillips", display: "Zoe Phillips" },
{ partyId: "P014", first: "Holly", last: "Barry", display: "Holly Barry" },
{ partyId: "P014", first: "Odhran", last: "Brosnan", display: "Odhran Brosnan" },
{ partyId: "P015", first: "Gerard", last: "Mulqueen", display: "Gerard Mulqueen" },
{ partyId: "P015", first: "Edwina", last: "Mulqueen", display: "Edwina Mulqueen" },
{ partyId: "P016", first: "Joe", last: "Barry", display: "Joe Barry" },
{ partyId: "P016", first: "Martina", last: "Barry", display: "Martina Barry" },
{ partyId: "P016", first: "Jason", last: "Barry", display: "Jason Barry" },
{ partyId: "P016", first: "Aimee", last: "Barry", display: "Aimee Barry" },
{ partyId: "P017", first: "Wendy", last: "McPherson", display: "Wendy McPherson" },
{ partyId: "P017", first: "John", last: "McPherson", display: "John McPherson" },
{ partyId: "P018", first: "Barbara", last: "Hylands", display: "Barbara Hylands" },
{ partyId: "P019", first: "Greg", last: "Lindsell", display: "Greg Lindsell" },
{ partyId: "P019", first: "Cathrine", last: "Lindsell", display: "Cathrine Lindsell" },
{ partyId: "P020", first: "Jean", last: "Marsh", display: "Jean Marsh" },
{ partyId: "P021", first: "Katie", last: "Shiels", display: "Katie Shiels" },
{ partyId: "P021", first: "Cathal", last: "Gavin", display: "Cathal Galvin" },
{ partyId: "P022", first: "Davida", last: "Shiels", display: "Davida Shiels" },
{ partyId: "P022", first: "James", last: "Flanagan", display: "James Flanagan" },
{ partyId: "P023", first: "Kathleen", last: "Forde", display: "Kathleen Forde" },
{ partyId: "P023", first: "David ", last: "Forde", display: "David Forde" },
{ partyId: "P023", first: "Shauna", last: "Forde", display: "Shauna Forde" },
{ partyId: "P024", first: "Katie", last: "O'Shea", display: "Katie O'Shea" },
{ partyId: "P024", first: "Colm", last: "O'Shea", display: "Colm O'Shea" },
{ partyId: "P025", first: "Clodagh", last: "McCarthy", display: "Clodagh McCarthy" },
{ partyId: "P025", first: "Peter", last: "McCarthy", display: "Peter McCarthy" },
{ partyId: "P026", first: "Mary", last: "Foley", display: "Mary Foley" },
{ partyId: "P026", first: "David", last: "Foley", display: "David Foley" },
{ partyId: "P027", first: "Dani", last: "Murphy", display: "Dani Murphy" },
{ partyId: "P027", first: "Declan", last: "Murphy", display: "Declan Murphy" },
{ partyId: "P028", first: "Victoria", last: "Boyle", display: "Victoria Boyle" },
{ partyId: "P028", first: "James", last: "Clarke", display: "James Clarke" },
{ partyId: "P029", first: "Victoria", last: "Mulqueen", display: "Victoria Mulqueen" },
{ partyId: "P029", first: "Dylan", last: "MacLochlainn", display: "Dylan MacLochlainn" },
{ partyId: "P030", first: "Ciara", last: "McInerney", display: "Ciara McInerney" },
{ partyId: "P031", first: "Rosie", last: "Gannon", display: "Rosie Gannon" },
{ partyId: "P032", first: "Ellie", last: "Turner", display: "Ellie Turner" },
{ partyId: "P033", first: "Rebecca", last: "Elliott", display: "Rebecca Elliott" },
{ partyId: "P034", first: "Amandine", last: "Millois", display: "Amandine Millois" },
{ partyId: "P035", first: "John", last: "Foster", display: "John Foster" },
{ partyId: "P035", first: "Laura", last: "Oliver", display: "Laura Oliver" },
{ partyId: "P036", first: "Nic", last: "Derrane", display: "Nic Derrane" },
{ partyId: "P036", first: "Emma", last: "Derrane", display: "Emma Derrane" },
{ partyId: "P037", first: "Simon", last: "Wells", display: "Simon Wells" },
{ partyId: "P038", first: "Hetty", last: "Quirke", display: "Hetty Quirke" },
{ partyId: "P038", first: "Alfie", last: "Batten", display: "Alfie Batten" },
{ partyId: "P039", first: "Kay", last: "Nicholas", display: "Kay Nicholas" },
{ partyId: "P040", first: "Alex", last: "Hayes", display: "Alex Hayes" },
{ partyId: "P041", first: "Dan", last: "Barnes", display: "Dan Barnes" },
{ partyId: "P041", first: "Pamela", last: "Barnes", display: "Pamela Barnes" },
{ partyId: "P042", first: "Charlie", last: "Fraser", display: "Charlie Fraser" },
{ partyId: "P042", first: "Matt", last: "Renvoize", display: "Matt Renvoize" },
{ partyId: "P043", first: "Kaya", last: "Affleck", display: "Kaya Affleck" },
{ partyId: "P043", first: "Johnny", last: "Davidson", display: "Johnny Davidson" },
{ partyId: "P044", first: "Claire", last: "Simpson", display: "Claire Simpson" },
{ partyId: "P044", first: "Neil", last: "Simpson", display: "Neil Simpson" },
{ partyId: "P045", first: "Rebecca", last: "Mulqueen", display: "Rebecca Mulqueen" },
{ partyId: "P045", first: "Alejandro", last: "Sarta Barba", display: "Alejandro Sarta Barba" },
{ partyId: "P046", first: "Alison", last: "O'Callaghan", display: "Alison O'Callaghan" },
{ partyId: "P046", first: "Mick", last: "O'Callaghan", display: "Mick O'Callaghan" },
{ partyId: "P047", first: "Walter", last: "Mirarchi", display: "Walter Mirarchi" },
{ partyId: "P047", first: "Ivana", last: "", display: "Ivana" },
{ partyId: "P048", first: "Isabel", last: "Wukonigg", display: "Isabel Wukonigg" },
];

// Normalise names for matching (case-insensitive, ignores extra spaces/punctuation)
function normaliseName(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z\s'-]/g, ""); // keep letters/spaces/'/-
}

function makeGoogleCalendarUrl({ title, details, location, startISO, endISO }) {
  const base = "https://calendar.google.com/calendar/render?action=TEMPLATE";

  // Google wants YYYYMMDDTHHMMSSZ (UTC) — we’ll pass UTC times for simplicity
  const start = new Date(startISO).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const end = new Date(endISO).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

  const params = new URLSearchParams({
    text: title,
    details,
    location,
    dates: `${start}/${end}`,
  });

  return `${base}&${params.toString()}`;

}
function findPartyByName(inputFullName) {
  const target = normaliseName(inputFullName);
  if (!target) return null;

  const match = RSVP_GUESTS.find((g) => {
    const full = normaliseName(`${g.first} ${g.last}`);
    return full === target;
  });

  if (!match) return null;

  return RSVP_GUESTS.filter((g) => g.partyId === match.partyId);
}

/* ================= PAGES ================= */

function PageHeader({ title, imageSrc, alt }) {
  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 pt-10">
      <div className="flex items-center gap-4">
        {imageSrc ? (
<img
  src={imageSrc}
  alt={alt || title}
  className="h-55 sm:h-55 w-40 sm:w-56 rounded-2xl object-cover shadow"
/>
        ) : null}

        <h2
          className="font-serif text-2xl sm:text-4xl md:text-5xl text-center leading-tight break-words px-4"
          style={{ color: THEME.sage }}
        >
          {title}
        </h2>
      </div>
    </div>
  );
}

function HomePage() {
  const { days, hours, minutes, seconds } = useCountdown(WEDDING.dateISO);
  const [heroLoaded, setHeroLoaded] = useState(false);

  return (
    <>
{/* HOME HERO BANNER ONLY */}
<div className="relative h-[50vh] sm:h-[60vh] lg:h-[70vh] w-full overflow-hidden">
  <motion.img
    src="/chapel house ll art.webp"
    alt="Alec & Sarah wedding invitation artwork"
    className="absolute inset-0 h-full w-full object-cover object-center"
    loading="eager"
    onLoad={() => setHeroLoaded(true)}
    initial={{ opacity: 0, scale: 1.04, filter: "blur(10px)" }}
    animate={
      heroLoaded
        ? { opacity: 1, scale: 1, filter: "blur(0px)" }
        : { opacity: 0, scale: 1.04, filter: "blur(10px)" }
    }
    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
  />

  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-white/0" />
</div>

{/* Wedding Title Block */}
<motion.div
  className="relative text-center py-16 sm:py-20 overflow-hidden"
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ duration: 1.4 }}
>

  {/* Soft ivory gradient background */}
<div
  className="absolute inset-0"
  style={{
    background: "linear-gradient(to bottom, #FDF9F4 0%, #FDF9F4 70%, rgba(253,249,244,0) 100%)"
  }}
/>

  {/* Botanical watermark */}
  <img
    src="/butterfly-logo.png"
    alt=""
    aria-hidden="true"
    className="absolute opacity-5 w-[420px] max-w-none
               left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2
               pointer-events-none select-none"
  />

  {/* Content layer */}
  <div className="relative max-w-3xl mx-auto px-6">

    <p
      className="text-lg uppercase tracking-[0.35em] mb-4"
      style={{ color: THEME.sage }}
    >
      The Wedding of
    </p>

    <h1
      className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight"
      style={{ color: THEME.cornflower }}
    >
      Alec & Sarah
    </h1>

    <p
      className="mt-4 text-sm sm:text-base tracking-wide"
      style={{ color: THEME.rose }}
    >
      13th August 2026
    </p>

    <div
      className="mt-6 h-px w-20 mx-auto"
      style={{ backgroundColor: THEME.buttercup }}
    />

  </div>

</motion.div>


<div className="flex justify-center pb-16">
  <img
    src="/couple-photo.jpg"
    alt="Alec and Sarah"
    className="rounded-3xl shadow-xl max-h-[420px] object-cover"
  />
</div>


      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
        {[
          { label: "Days", value: days, color: THEME.cornflower },
          { label: "Hours", value: hours, color: THEME.lavender },
          { label: "Minutes", value: minutes, color: THEME.sage },
          { label: "Seconds", value: seconds, color: THEME.rose },
        ].map((item) => (
          <Card key={item.label} className="p-5 text-center">
            <div className="font-serif text-3xl" style={{ color: item.color }}>
              {String(item.value).padStart(2, "0")}
            </div>
            <div className="text-xs uppercase tracking-wide text-slate-600">{item.label}</div>
          </Card>
        ))}
      </div>

      <Divider />

      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
<a
  href="/rsvp"
  className="flex w-full sm:w-auto items-center justify-center gap-3 rounded-3xl px-10 py-5 text-lg sm:text-xl font-semibold text-white shadow-xl hover:opacity-95 transition hover:-translate-y-0.5 hover:shadow-2xl"
  style={{ backgroundColor: THEME.rose }}
>
  RSVP here
</a>
        <a href="/details" className="inline-flex items-center gap-2 rounded-2xl px-6 py-3 border shadow hover:bg-slate-50 transition bg-white">
          <Info className="h-4 w-4" /> Wedding Details
        </a>
      </div>
{/* Artwork credit */}
<div className="mt-10 flex justify-center">
  <div className="text-center">
    <p
      className="text-xs sm:text-sm italic leading-snug"
      style={{ color: THEME.sage }}
    >
      All artwork is original
      <br />
      and kindly supplied by <span className="font-semibold">Lucy Lindsell</span>
    </p>

    <img
      src="/Lucy Lindsell Signature.webp"
      alt="Lucy Lindsell signature"
      className="mt-1 w-32 sm:w-36 opacity-100 relative left-1/2 -translate-x-1/2"
    />
  </div>
</div>

    </>
  );
}

function DetailsPage() {
  return (
    <>
      <PageHeader
        title="Wedding Details"
        imageSrc="flower2.webp"
        alt="flower2.webp"
      />

      <Section className="py-12">
      <h2 className="font-serif text-3xl text-center" style={{ color: THEME.cornflower }}>
        
      </h2>

      <div className="grid gap-6 md:grid-cols-2 items-stretch">
        <Card className="p-7 h-full">
          <h3 className="font-serif text-xl mb-2" style={{ color: THEME.cornflower }}>
    Wedding Details
  </h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Calendar className="h-4 w-4 mt-0.5" />
              <span>
                <strong>Date & Time:</strong> {WEDDING.datePretty} at 2:00 PM (arrivals from 1:30 PM)
              </span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-0.5" />
              <span>
                <strong>Venue:</strong> {WEDDING.venueName}, {WEDDING.venueAddress} —{" "}
                <a className="underline" href={WEDDING.venueMapUrl} target="_blank" rel="noreferrer">
                  View map
                </a>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="h-4 w-4 mt-0.5" />
              <span>
                <strong>Dress Code:</strong> Smart / smart casual / garden party - We want you to be comfortable
              </span>
            </li>
          </ul>
        </Card>

{/* Add to Calendar */}
<Card className="p-7 h-full">
  <h3 className="font-serif text-xl mb-2" style={{ color: THEME.cornflower }}>
    Add to calendar
  </h3>
  <p className="text-sm text-slate-600 mb-4">
    Pop the day in your diary so you don’t accidentally do something else.
  </p>

  <div className="flex flex-col sm:flex-row gap-3">
    {/* Google Calendar */}
    <a
      href={makeGoogleCalendarUrl({
        title: "Alec & Sarah’s Wedding",
        details: "Wedding celebration for Alec & Sarah. Details at alecandsarah.wedding",
        location: "Chapel House Estate, Ramsgate, Kent",
        // starts 13:30 UK time -> using ISO with Z will be treated as UTC, but it’s fine for a quick add
        // If you want exact UK-time handling, we can do that next.
        startISO: "2026-08-13T12:30:00Z",
        endISO: "2026-08-13T23:00:00Z",
      })}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3 text-white shadow hover:opacity-95 transition"
      style={{ backgroundColor: THEME.rose }}
    >
      Add to Google Calendar
    </a>

    {/* Apple / Outlook / downloadable */}
    <a
      href="/alec-and-sarah-wedding.ics"
      className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3 border shadow hover:bg-slate-50 transition bg-white"
      style={{ borderColor: THEME.sage }}
    >
      Download calendar file (Apple/Outlook)
    </a>
  </div>
</Card>

        <Card className="p-7 md:col-span-2 mt-6">
  <h3 className="font-serif text-2xl mb-6" style={{ color: THEME.cornflower }}>
    Our Story
  </h3>

  <div className="space-y-5 text-sm sm:text-base leading-relaxed text-slate-700">
    <p>Back in 2022, we met on the dating app Hinge and immediately hit it off.
  </p>

  <p>
    A first date was quickly arranged at the most exclusive place in London we both knew — 
    The Plough in Clapham. Sharing similar interests in music, adventure, films, 
    animals and much more — not to mention a very dark sense of humour — it was 
    clear from the start that we were onto something special.
  </p>

  <p>
    Naturally, not wanting to rush things… six months later Sarah moved in with 
    Alec and his grumpy cat, George and never looked back.
  </p>

  <p>
    Over the years we have shared some lovely adventures; been to great gigs,  beautiful  
    places, and eaten a lot of tasty food. We’ve ridden 
    crazy rollercoasters, danced on a glacier, and triumphed in pub quizzes, 
    although Sarah mostly only attends those for the wine.
  </p>

  <p>
    With lots of laughs between us, bad jokes told, many miles travelled, and  
    fantastic days had, not to mention the addition of two new naughty pussycats, 
    our relationship could not be better. So in March 2025, off the Wild 
    Atlantic coast of Ireland, Alec proposed and Sarah said yes!
  </p>

  <p>
    If you’re reading this, then it means you’re one of our favourite people, and we’d love for 
    you to share our special day. There will be fun, good food and drink, and 
    plenty of dancing, so we hope you can join us as we celebrate our marriage.
  </p>

  <p className="italic">
    Cheers and Sláinte - Alec & Sarah
  </p>
        </div>
        </Card>
      </div>
        </Section>

<Section className="py-12">
  <div className="max-w-5xl mx-auto">
    <h2
      className="font-serif text-3xl mb-6 text-center"
      style={{ color: THEME.cornflower }}
    >
      About Chapel House Estate
    </h2>

    {/* Wide image */}
    <div className="mb-8">
      <img
        src="/venue/chapel-house-wide.jpg"
        alt="Chapel House Estate"
        className="w-full rounded-3xl shadow-lg object-cover"
        loading="lazy"
      />
    </div>

    <div className="grid md:grid-cols-2 gap-8 items-center">
      {/* Text */}
      <div className="text-sm leading-relaxed">
        <p className="mb-4">
          Chapel House Estate is an historic country estate nestled in the Kent
          countryside, with roots stretching back several centuries. Once a
          working agricultural estate, it has been thoughtfully restored to
          celebrate its heritage with the Grade II-listed Chapel, built in 1290
          forming a stunning centrepiece. 
        </p>

        <p className="mb-4">
          Surrounded by orchards, gardens and open fields, the estate blends
          rustic charm with understated elegance. It’s a place shaped by time,
          nature and craftsmanship, the moment we arrived here, we knew this was the place, 
          and it feels incredibly special to be celebrating our wedding here. 
        </p>
      </div>

      {/* Secondary image */}
      <div>
        <img
          src="/venue/chapel-house-med.jpg"
          alt="Chapel House Estate gardens"
          className="w-full rounded-3xl shadow-lg object-cover"
          loading="lazy"
        />
      </div>
    </div>
            <Divider />
  </div>
</Section>
    </>
  );
}
function SchedulePage() {
  return (
    <>
      <PageHeader
        title="Schedule"
        imageSrc="/bluebell.webp"
        alt="Schedule artwork"
      />

      <Section className="py-12">
      <h2 className="font-serif text-3xl text-center" style={{ color: THEME.cornflower }}>
        Schedule
      </h2>
      <p className="mt-2 text-center text-sm text-slate-600">
        Final timings may shift slightly — we’ll keep this page updated.
      </p>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
  {/* LEFT: Wedding day schedule */}
  <Card className="p-7">
  <h3
    className="font-serif text-xl mb-4"
    style={{ color: THEME.cornflower }}
  >
    The Wedding (Thursday)
  </h3>
    <h3 className="font-serif text-xl" style={{ color: THEME.rose }}>
      Daytime
    </h3>

    <div className="mt-4 space-y-4 text-sm">
      {[
        ["13:30", "Guests arrive"],
        ["14:00", "Ceremony"],
        ["14:30", "Drinks and canapés"],
        ["16:00", "Dinner"],
      ].map(([t, d]) => (
        <div key={`day-${t}`} className="flex items-start justify-between gap-4">
          <p className="font-medium">{t}</p>
          <p className="text-slate-600 text-right">{d}</p>
        </div>
      ))}
    </div>

    <div className="mt-8 pt-6 border-t">
      <h3 className="font-serif text-xl" style={{ color: THEME.lavender }}>
        Evening
      </h3>

      <div className="mt-4 space-y-4 text-sm">
        {[
          ["19:00", "Dancing and Party Time"],
          ["21:00", "Evening Refreshment"],
          ["00:00", "Carriages"],
        ].map(([t, d]) => (
          <div key={`eve-${t}`} className="flex items-start justify-between gap-4">
            <p className="font-medium">{t}</p>
            <p className="text-slate-600 text-right">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </Card>

  {/* RIGHT: Friday info */}
  <Card className="p-7">
    <h3 className="font-serif text-xl" style={{ color: THEME.cornflower }}>
      Friday
    </h3>

    <img
      src="/royal-albion.jpg"
      alt="The Royal Albion Hotel, Broadstairs"
      className="mt-4 rounded-2xl w-full h-48 object-cover"
      loading="lazy"
    />

    <p className="mt-4 text-sm leading-relaxed text-slate-700">
      We hope that you all had a great time celebrating us getting hitched — and
      for those of you who are not too wrecked and want to carry on, we’d
      love to invite all who can make it to <strong>The Royal Albion Hotel</strong> in
      Broadstairs from <strong>1pm</strong> where there will be some nibbles and more drinks.
    </p>

    <a
      href="https://maps.app.goo.gl/rz3Yvf9d6S4L9eTW7"
      target="_blank"
      rel="noreferrer"
      className="mt-5 inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm text-white shadow hover:opacity-95 transition"
      style={{ backgroundColor: THEME.rose }}
    >
      View on map
    </a>
  </Card>
</div>
<Divider />
      </Section>
    </>
  );
}

function RSVPPage() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
const [alreadyData, setAlreadyData] = useState(null); // holds webhook result if already submitted
const [checkError, setCheckError] = useState("");


async function handleSubmit(e) {
  e.preventDefault();
  setError("");
  setCheckError("");
  setAlreadyData(null);

  const party = findPartyByName(fullName);

  if (!party) {
    setError(
      "Sorry — we couldn’t find that name. Please check spelling and try again (first + last name)."
    );
    return;
  }

  const partyId = party[0]?.partyId;
  if (!partyId) {
    setCheckError("Sorry — something is missing for this invite (partyId). Please message us.");
    return;
  }

  try {
    setChecking(true);

    const res = await fetch(
      `/.netlify/functions/rsvpCheck?partyId=${encodeURIComponent(partyId)}`
    );
    const data = await res.json().catch(() => ({}));

    if (!res.ok || data.ok === false) {
      throw new Error(data.error || "Check failed");
    }


    if (data.alreadySubmitted) {
      // ✅ show message instead of allowing another RSVP
      setAlreadyData(data);
      return;
    }

    // Not submitted yet → proceed
    localStorage.setItem("rsvpParty", JSON.stringify(party));
    navigate("/rsvp/party");
  } catch (err) {
    console.error(err);
    setCheckError(
      "Sorry — we couldn't check your RSVP status. Please try again in a moment."
    );
  } finally {
    setChecking(false);
  }
}


  return (
    <>
      <PageHeader title="RSVP" imageSrc="leaves2.webp" alt="RSVP artwork" />

      <Section className="py-12">
        <Card className="p-7 max-w-2xl mx-auto">
          <h3 className="font-serif text-2xl mb-2" style={{ color: THEME.cornflower }}>
            RSVP
          </h3>

          <p className="text-sm leading-relaxed">
            Please enter the <strong>first and last name</strong> of one member of your party below.
            If you're responding for you and a guest (or your family), you'll be able to RSVP for your
            entire group on the next page.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm mb-2">First and last name</label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="e.g. John Smith"
                className="w-full rounded-2xl border px-4 py-3 bg-white"
              />
            </div>

            {error && (
              <div className="text-sm rounded-2xl px-4 py-3 bg-red-50 border border-red-200 text-red-700">
                {error}
              </div>
            )}

{checkError && (
  <div className="text-sm rounded-2xl px-4 py-3 bg-red-50 border border-red-200 text-red-700">
    {checkError}
  </div>
)}

{alreadyData && (
  <div className="text-sm rounded-2xl px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-800">
    <div className="font-semibold">RSVP already recorded ✅</div>
    <div className="mt-1">
      We’ve already received an RSVP for your party (ID: <strong>{alreadyData.partyId}</strong>).
      If you need to change anything, please message us.
    </div>
  </div>
)}

            <button
  type="submit"
  disabled={checking}
  className={`w-full sm:w-auto inline-flex items-center justify-center rounded-3xl px-8 py-4 text-base font-semibold text-white shadow-xl hover:opacity-95 transition ${
    checking ? "opacity-60 cursor-not-allowed" : ""
  }`}
  style={{ backgroundColor: THEME.rose }}
>
  {checking ? "Checking…" : "Find my invite"}
</button>


            <p className="text-xs text-slate-600 pt-2">
              If you’re having trouble, please message us and we’ll help you out.
            </p>
          </form>
        </Card>
        <Divider />
      </Section>
    </>
  );
}

function PartyRSVPPage() {
  const navigate = useNavigate();
const [party, setParty] = useState([]);
const [responses, setResponses] = useState({});
const [contactEmail, setContactEmail] = useState("");
const [notes, setNotes] = useState("");
const [formError, setFormError] = useState("");
const [isSubmitting, setIsSubmitting] = useState(false);
const [alreadyData, setAlreadyData] = useState(null);
const [submitSuccess, setSubmitSuccess] = useState(false);
const [submitOutcome, setSubmitOutcome] = useState(null);



const canSubmit =
  party.length > 0 &&
  party.every(
    (g) =>
      responses[g.display]?.attending === true ||
      responses[g.display]?.attending === false
  );



  useEffect(() => {
    const stored = localStorage.getItem("rsvpParty");
    if (!stored) {
      navigate("/rsvp");
      return;
    }

    const parsed = JSON.parse(stored);
    setParty(parsed);

    // initialise responses
const initial = {};
parsed.forEach((guest) => {
  initial[guest.display] = {
    attending: null,
    transport: "own", // default = arranging own transport
  };
});
setResponses(initial);

  }, [navigate]);

function updateResponse(name, patch) {
  setResponses((prev) => ({
    ...prev,
    [name]: {
      ...prev[name],
      ...patch,
    },
  }));
}

async function handleSubmit(e) {
  e.preventDefault();
  if (isSubmitting) return;
setIsSubmitting(true);
  setFormError("");

  const unanswered = party.filter(
  (g) => responses[g.display]?.attending !== true && responses[g.display]?.attending !== false
);

  if (unanswered.length > 0) {
    setFormError("Please answer Yes/No for everyone in your party before submitting.");
    return;
  }

  const payload = {
    submittedAt: new Date().toISOString(),
    partyId: party[0]?.partyId,
    contactEmail,
    notes,
    responses,
  };

  try {
    const res = await fetch("/.netlify/functions/rsvp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
console.log("RSVP function response:", res.status, data);

    // if the backend says "already submitted", show that state and stop
    if (data.alreadySubmitted) {
      setAlreadyData(data);
      setIsSubmitting(false);
      return;
    }

// Work out whether anyone in the party is attending
const anyAttending = Object.values(responses).some(
  (r) => r.attending === true
);

console.log("anyAttending =", anyAttending);
console.log("responses =", responses);
console.log("submitSuccess value being set to =", anyAttending ? "yes" : "no");
setSubmitSuccess(anyAttending ? "yes" : "no");

if (anyAttending && typeof window !== "undefined") {
  import("canvas-confetti").then(({ default: confetti }) => {
    confetti({
      particleCount: 90,
      spread: 70,
      origin: { y: 0.65 },
    });
  });
}

    // Clear stored party so they can't resubmit easily
    localStorage.removeItem("rsvpParty");

    setIsSubmitting(false);

    // OPTIONAL: redirect AFTER they see message
    // If you want them to stay on the success screen, DELETE this setTimeout.
    setTimeout(() => {
      navigate("/");
    }, 5000);

    return;


// OPTIONAL redirect after 3 seconds
setTimeout(() => {
  navigate("/");
}, 3000);

  } catch (err) {
    setIsSubmitting(false);
    setFormError(
      "Sorry — something went wrong submitting your RSVP. Please try again, or message us."
    );
    console.error(err);
  }
}

  return (
    <>
      <PageHeader title="Your RSVP" imageSrc="/Rings.webp" alt="Your RSVP" />

      <Section className="py-12">
        <Card className="p-7 max-w-2xl mx-auto">
         {!submitSuccess && !alreadyData && (
  <h3
    className="font-serif text-2xl mb-4"
    style={{ color: THEME.cornflower }}
  >
    Please let us know who will be attending
  </h3>
)}

{alreadyData ? (
  <div className="mb-5 rounded-2xl border px-4 py-3 text-sm bg-white/60">
    <p className="font-medium mb-2">We already have your RSVP ✅</p>

    <div className="space-y-2">
      {Object.entries(alreadyData.responses || {}).map(([name, r]) => (
        <div key={name} className="flex items-start justify-between gap-4">
          <span>{name}</span>
          <span className="text-slate-600 text-right">
            {r.attending ? "Attending" : "Not attending"} •{" "}
            {r.transport === "bus" ? "Midnight bus" : "Own transport"}
          </span>
        </div>
      ))}
    </div>

    <p className="mt-3 text-xs text-slate-600">
      Need to change something? Message us and we’ll update it.
    </p>
  </div>
) : submitSuccess ? (
  <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-6 py-6 text-center shadow-sm animate-fadeIn">
    <div className="text-3xl mb-2">
      {submitSuccess === "yes" ? "✨" : "💛"}
    </div>

    {submitSuccess === "yes" ? (
      <>
        <h3 className="font-serif text-xl mb-2" style={{ color: THEME.sage }}>
          We can’t wait to celebrate with you
        </h3>
        <p className="text-sm text-emerald-900">
          Your RSVP has been successfully recorded.
        </p>
      </>
    ) : (
      <>
        <h3 className="font-serif text-xl mb-2" style={{ color: THEME.rose }}>
          We’re sorry you’re not able to attend
        </h3>
        <p className="text-sm text-emerald-900">
          You will be missed.
        </p>
      </>
    )}
  </div>
) : (
  <form onSubmit={handleSubmit} className="space-y-4">
{party.map((guest) => {
  const r = responses[guest.display] || {};
  const attending = r.attending; // true/false/null
  const transport = r.transport || "own"; // "bus" | "own"

  const pillBase =
    "inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-medium border transition whitespace-nowrap";
  const pillOff = "bg-white hover:opacity-90";
  const pillOn = "text-white";

  return (
    <div
      key={guest.display}
      className="rounded-3xl border bg-white/70 backdrop-blur-md px-5 py-4"
    >
      <div className="grid gap-4 sm:grid-cols-[220px_1fr_1fr] sm:items-center">
        {/* Name */}
        <div className="text-sm font-semibold">
          {guest.display}
        </div>

        {/* Attending */}
        <div className="flex flex-wrap gap-3 sm:justify-center">
          <button
            type="button"
            onClick={() => updateResponse(guest.display, { attending: true })}
            className={`${pillBase} ${
              attending === true ? pillOn : pillOff
            }`}
            style={
              attending === true
                ? { backgroundColor: THEME.sage, borderColor: THEME.sage }
                : { borderColor: THEME.sage, color: THEME.text }
            }
          >
            Yes
          </button>

          <button
            type="button"
            onClick={() => updateResponse(guest.display, { attending: false })}
            className={`${pillBase} ${
              attending === false ? pillOn : pillOff
            }`}
            style={
              attending === false
                ? { backgroundColor: THEME.buttercup, borderColor: THEME.buttercup }
                : { borderColor: THEME.buttercup, color: THEME.text }
            }
          >
            No
          </button>
        </div>

        {/* Transport */}
        <div className="flex flex-col sm:items-end gap-3">
          <div className="flex flex-wrap gap-3 sm:justify-end">
            <button
              type="button"
              onClick={() => updateResponse(guest.display, { transport: "bus" })}
              className={`${pillBase} ${
                transport === "bus" ? pillOn : pillOff
              } min-w-[220px]`}
              style={
                transport === "bus"
                  ? { backgroundColor: THEME.sage, borderColor: THEME.sage }
                  : { borderColor: THEME.sage, color: THEME.text }
              }
            >
              <div className="flex items-center gap-2">
  <img
    src="/Carriage.webp"
    alt="Carriage icon"
    className="h-4 w-4 object-contain"
  />
  <span>I’d like the midnight bus</span>
</div>
            </button>

            <button
              type="button"
              onClick={() => updateResponse(guest.display, { transport: "own" })}
              className={`${pillBase} ${
                transport === "own" ? pillOn : pillOff
              } min-w-[220px]`}
              style={
                transport === "own"
                  ? { backgroundColor: THEME.sage, borderColor: THEME.sage }
                  : { borderColor: THEME.sage, color: THEME.text }
              }
            >
              I’ll arrange my own transport
            </button>
          </div>

          <p className="text-xs text-slate-600">
            (If you’re not attending, transport won’t be counted.)
          </p>
        </div>
      </div>
    </div>
  );
})}

{submitSuccess && (
  <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-6 py-6 text-center shadow-sm animate-fadeIn">
    <div className="text-3xl mb-2">
      {submitSuccess === "yes" ? "✨" : "💛"}
    </div>

    {submitSuccess === "yes" ? (
      <>
        <h3 className="font-serif text-xl mb-2" style={{ color: THEME.sage }}>
          We can't wait to celebrate with you
        </h3>
        <p className="text-sm text-emerald-900">
          Your RSVP has been successfully recorded.
        </p>
      </>
    ) : (
      <>
        <h3 className="font-serif text-xl mb-2" style={{ color: THEME.rose }}>
          We're sorry you're not able to attend
        </h3>
        <p className="text-sm text-emerald-900">
          You will be missed.
        </p>
      </>
    )}
  </div>
)}

{formError && (
      <div className="text-sm rounded-2xl px-4 py-3 bg-red-50 border border-red-200 text-red-700">
        {formError}
      </div>
    )}

{/* Contact + notes */}
<div className="rounded-3xl border bg-white/70 backdrop-blur-md px-5 py-5 space-y-4">
  <div>
    <label htmlFor="rsvp-email" className="block text-sm font-medium mb-2">
      Email address (optional)
    </label>
    <input
      id="rsvp-email"
      name="rsvpEmail"
      type="email"
      value={contactEmail}
      onChange={(e) => setContactEmail(e.target.value)}
      placeholder="e.g. name@email.com"
      className="w-full rounded-2xl border px-4 py-3 bg-white"
    />
    <p className="mt-1 text-xs text-slate-600">
      Helpful in case we need to check anything.
    </p>
  </div>

  <div>
    <label htmlFor="rsvp-notes" className="block text-sm font-medium mb-2">
      Notes (dietary needs / anything we should know) (optional)
    </label>
    <textarea
      id="rsvp-notes"
      name="rsvpNotes"
      value={notes}
      onChange={(e) => setNotes(e.target.value)}
      placeholder="e.g. vegetarian, allergies, access needs…"
      rows={4}
      className="w-full rounded-2xl border px-4 py-3 bg-white"
    />
  </div>
</div>

    <button
      type="submit"
      disabled={!canSubmit || isSubmitting}
      className="w-full sm:w-auto mt-6 inline-flex items-center justify-center rounded-3xl px-8 py-4 text-base font-semibold text-white shadow-xl transition
                 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ backgroundColor: THEME.rose }}
    >
      {isSubmitting ? "Submitting…" : "Submit RSVP"}
    </button>
  </form>
)}
        </Card>
        <Divider />
      </Section>
    </>
  );
}

function AdminPage() {
    const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/.netlify/functions/rsvpSummary");
        const data = await res.json();
        if (!res.ok || data.ok === false) throw new Error(data.error || "Failed to load summary");
        setSummary(data);
      } catch (err) {
        setSummaryError(String(err));
      }
    })();
  }, []);

  const [ok, setOk] = useState(() => localStorage.getItem("adminOK") === "true");
  const [code, setCode] = useState("");

  function handleUnlock(e) {
    e.preventDefault();
    if (code.trim() === ADMIN.code) {
      localStorage.setItem("adminOK", "true");
      setOk(true);
    }
  }

  function handleLock() {
    localStorage.removeItem("adminOK");
    setOk(false);
    setCode("");
  }

  if (!ok) {
    return (
      <>
        <PageHeader title="Admin" imageSrc="leaves2.webp" alt="Admin" />
        <Section className="py-12">
          <Card className="p-7 max-w-md mx-auto">
            <h3 className="font-serif text-2xl mb-3" style={{ color: THEME.cornflower }}>
              Admin access
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Enter the admin code to view guest totals.
            </p>

            <form onSubmit={handleUnlock} className="space-y-3">
              <input
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Admin code"
                className="w-full rounded-2xl border px-4 py-3 text-sm"
              />

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center rounded-3xl px-8 py-4 text-base font-semibold text-white shadow-xl hover:opacity-95 transition"
                style={{ backgroundColor: THEME.rose }}
              >
                Unlock
              </button>

            </form>
          </Card>
        </Section>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Admin Dashboard" imageSrc="flowers 1.webp" alt="Admin" />
      <Section className="py-12">
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={handleLock}
            className="rounded-2xl px-4 py-2 text-sm border bg-white hover:opacity-80"
          >
            Lock admin
          </button>
        </div>
        {summaryError && (
  <p className="mb-4 text-sm" style={{ color: THEME.rose }}>
    {summaryError}
  </p>
)}


        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-7">
            <h3 className="font-serif text-xl" style={{ color: THEME.cornflower }}>Attending</h3>
            <p className="mt-3 text-4xl font-semibold">
  {summary ? summary.attendingYes : "…"}
</p>
          </Card>

          <Card className="p-7">
            <h3 className="font-serif text-xl" style={{ color: THEME.lavender }}>Not attending</h3>
            <p className="mt-3 text-4xl font-semibold">
  {summary ? summary.attendingNo : "…"}
</p>
          </Card>

          <Card className="p-7">
            <h3 className="font-serif text-xl" style={{ color: THEME.rose }}>Midnight bus</h3>
            <p className="mt-3 text-4xl font-semibold">
  {summary ? summary.busCount : "…"}
</p>
          </Card>
        </div>

        <Card className="p-7 mt-6">
          <h3 className="font-serif text-xl mb-2" style={{ color: THEME.sage }}>
            Notes
          </h3>
          <p className="text-sm text-slate-600">
            Next step: we’ll wire these totals to your Google Sheet via a Netlify function (no CORS, no exposing secrets).
          </p>
        </Card>
      </Section>
    </>
  );
}


function TravelPage() {
  const [mapOpen, setMapOpen] = useState(false);
  return (
    <>
      <PageHeader
        title="Travel & Accommodation"
        imageSrc="Carriage.webp"
        alt="Travel artwork"
      />

      <Section className="py-12">


      <div className="grid md:grid-cols-2 gap-6">
        {/* On-site Pods */}
        <Card className="p-7">
          <h3 className="font-serif text-xl mb-2" style={{ color: THEME.cornflower }}>
            Accommodation at Chapel House Estate
          </h3>
          <p className="text-sm leading-relaxed">
            Chapel House Estate has a limited number of beautiful pods available
            for the <strong>night of the wedding only</strong>.
          </p>
          <p className="mt-3 text-sm leading-relaxed">
            These will be bookable directly with the venue using a{" "}
            <strong>special booking code</strong>.
          </p>
          <p className="mt-3 text-sm text-slate-600">
            Availability is limited and will be offered on a first-come,
            first-served basis. <strong>Please contact Alec or Sarah for the booking code before contacting the venue</strong>.
          </p>


        </Card>

        {/* Where to stay */}
        <Card className="p-7">
          <h3 className="font-serif text-xl mb-2" style={{ color: THEME.rose }}>
            Where to stay
          </h3>
          <p className="text-sm leading-relaxed">
            We’re encouraging guests to stay in <strong>Broadstairs</strong>,
            which is nearby and offers a great selection of Airbnbs, hotels, and
            B&Bs.
          </p>
          <p className="mt-3 text-sm leading-relaxed">
            While Broadstairs is our recommended location, there are also plenty
            of accommodation options in <strong>Ramsgate</strong> and{" "}
            <strong>Margate</strong>, all of which are close to the venue and
            easily reached by taxi.
          </p>
          <p className="mt-3 text-sm text-slate-600">
            <strong>August is a busy time of year, so we recommend booking early where
            possible. Please note that Broadstairs Folk Week is being held during the week of our wedding, which will make it busier than usual</strong>.
          </p>
        </Card>

        {/* Local Hotels */}
        <Card className="p-7">
  <h3 className="font-serif text-xl mb-2" style={{ color: THEME.lavender }}>
    Nearby hotels
  </h3>
  <p className="text-sm leading-relaxed">
    If you’d prefer a hotel, Thanet has a wide selection; here are a few dependable options which are near to the venue:
  </p>

  <div className="mt-5 space-y-4">
    {WEDDING.hotels.map((h) => (
      <div key={h.url} className="rounded-2xl border bg-white/60 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-medium">{h.name}</p>
            <p className="text-sm text-slate-600">{h.address}</p>
            <p className="mt-2 text-sm">{h.note}</p>
          </div>

          <a
            href={h.url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-2 rounded-xl px-4 py-2 text-white shadow hover:opacity-95 transition"
            style={{ backgroundColor: THEME.rose }}
          >
            View
          </a>
        </div>
      </div>
    ))}
  </div>
</Card>

<Card className="p-7">
  <h3 className="font-serif text-xl mb-2" style={{ color: THEME.cornflower }}>
    Train
  </h3>

  <p className="text-sm leading-relaxed">
    <strong>Broadstairs</strong>, <strong>Ramsgate</strong>, and{" "}
    <strong>Margate</strong> all have their own train stations with direct
    services to and from <strong>London</strong>.
  </p>

  <p className="mt-3 text-sm leading-relaxed">
    If you’re arriving on the morning of the wedding, the closest station to the
    venue is <strong>Thanet Parkway</strong>, which is just a{" "}
    <strong>5-minute taxi ride</strong> to Chapel House Estate.
  </p>

  <div className="mt-4">
    <a
      href="https://www.google.com/maps/search/?api=1&query=Thanet+Parkway+Station"
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-white shadow hover:opacity-95 transition"
      style={{ backgroundColor: THEME.rose }}
    >
      View Thanet Parkway on map
    </a>
  </div>

  <p className="mt-3 text-sm text-slate-600">
    Taxis are readily available from all stations - details are below.
  </p>
</Card>

{/* Getting there & taxis */}
<div className="md:col-span-2 grid md:grid-cols-2 gap-6">

  {/* Getting to the venue */}
  <Card className="p-7">
    <h3
      className="font-serif text-xl mb-3"
      style={{ color: THEME.sage }}
    >
      Getting to the venue
    </h3>

    <p className="text-sm text-slate-600 leading-relaxed">
      Chapel House Estate is approximately <strong>10–15 minutes by car</strong> from
      all areas around Thanet, including Broadstairs, Ramsgate and Margate.
    </p>

    <ul className="mt-4 text-sm space-y-2">
      <li>• Plenty of on-site parking is available for guests</li>
      <li>• There’s even a helipad if you’re feeling particularly bougie ✨</li>
    </ul>

<img
  src="/chapel-house-map.webp"
  alt="Map showing Chapel House Estate location"
  className="mt-5 rounded-2xl w-full object-cover cursor-zoom-in"
  loading="lazy"
  onClick={() => setMapOpen(true)}
/>

  </Card>

  {/* Taxi information */}
  <Card className="p-7">
    <h3
      className="font-serif text-xl mb-3"
      style={{ color: THEME.sage }}
    >
      Local taxis & ride-hailing
    </h3>

    <ul className="text-sm space-y-4">
      <li>
        <strong>Central Cars</strong> – operates 24/7 with offices in Margate,
        Broadstairs, and Ramsgate  
        <br />
        <span className="text-slate-600">Tel: 01843 888888</span>
      </li>

      <li>
        <strong>A2B Cars Thanet</strong> – based in Margate (Canterbury Road)  
        <br />
        <span className="text-slate-600">Tel: 01843 222222</span>
      </li>

      <li>
        <strong>Uber</strong> – operates in Margate and across the wider Thanet
        district
      </li>
    </ul>
  </Card>

</div>

      </div>
      {mapOpen && (
  <div
    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
    onClick={() => setMapOpen(false)}
  >
    <img
      src="/chapel-house-map.webp"
      alt="Chapel House Estate map enlarged"
      className="max-w-full max-h-full rounded-2xl shadow-2xl"
    />
  </div>
)}
<Divider />
      </Section>
    </>
  );
}

function ThingsCard({ title, description, image, website, map }) {
  return (
    <div className="rounded-3xl bg-white/70 backdrop-blur-md border border-white/60 shadow-xl overflow-hidden flex flex-col">
      {image && (
        <img
          src={image}
          alt={title}
          className="h-61 w-full object-cover"
          loading="lazy"
        />
      )}

      <div className="p-6 flex flex-col flex-grow">
        <h3 className="font-serif text-xl mb-2" style={{ color: THEME.cornflower }}>
          {title}
        </h3>

        <p className="text-sm leading-relaxed mb-4 flex-grow">
          {description}
        </p>

        <div className="flex gap-3 mt-auto">
          {website && (
            <a
              href={website}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white shadow hover:opacity-95 transition"
              style={{ backgroundColor: THEME.rose }}
            >
              Website
            </a>
          )}

          {map && (
            <a
              href={map}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm border shadow hover:bg-slate-50 transition"
            >
              View map
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ThingsToDoPage() {
  return (
    <>
      <PageHeader
        title="Things to do"
        imageSrc="/butterfly.webp"
        alt="Local attractions artwork"
      />

      <Section className="py-12">
        <p className="max-w-3xl mx-auto text-center text-sm mb-10">
          If you’re making a weekend of it, there’s plenty to see and do around
          Margate, Ramsgate, and Broadstairs. Here are a few local highlights we
          love.
        </p>

<div className="grid sm:grid-cols-2 gap-6">
  <ThingsCard
    title="Morelli's Gelato"
    image="/things-to-do/morellis.webp"
    description="An iconic gelateria in Broadstairs opened in 1932, serving delicious handmade gelato in a variety of classic and inventive flavours."
    website="https://www.morellisgelato.com/"
    map="https://maps.app.goo.gl/kAne4eZ6fzWGPWcc9"
  />

<ThingsCard
  title="Viking Bay"
  image="/things-to-do/viking-bay.jpeg"
  description="This magnificent horseshoe-shaped bay is the main attraction in Broadstairs. This was the holiday destination of choice for Charles Dickens and is also where the news of Wellington's victory at Waterloo first arrived in England"
  map="https://maps.app.goo.gl/a1qDHi91y9eMQS1K8"
/>

  <ThingsCard
    title="Dreamland Margate"
    image="/things-to-do/dreamland.jpg"
    description="Margate's historic amusement park with rides, roller discos, live music, and a great vintage seaside atmosphere."
    website="https://www.dreamland.co.uk"
    map="https://maps.google.com/?q=Dreamland+Margate"
  />

  <ThingsCard
    title="Botany Bay"
    image="/things-to-do/botany-bay.jpg"
    description="One of the most beautiful sandy beaches in Kent, known for its chalk cliffs, sea stacks, and clear water."
    map="https://maps.google.com/?q=Botany+Bay+Broadstairs"
  />

  <ThingsCard
    title="Margate Old Town"
    image="/things-to-do/margate-old-town.jpg"
    description="A charming historic quarter filled with independent shops, cafés, galleries, and vintage finds."
    map="https://maps.google.com/?q=Margate+Old+Town"
  />

  <ThingsCard
    title="The Margate Caves"
    image="/things-to-do/margate-caves.jpg"
    description="An underground chalk cave system beneath Margate, recently reopened and beautifully restored."
    website="https://margatecaves.co.uk"
    map="https://maps.google.com/?q=Margate+Caves"
  />

<ThingsCard
  title="Shell Grotto"
  image="/things-to-do/shell-grotto.jpg"
  description="A mysterious underground passageway decorated with millions of shells. One of Margate’s most curious and unique attractions."
  website="https://shellgrotto.co.uk"
  map="https://maps.google.com/?q=Shell+Grotto+Margate"
/>

  <ThingsCard
    title="Sea Scrub Sauna"
    image="/things-to-do/seascrub.webp"
    description="A unique wellness experience in Ramsgate, featuring a traditional Finnish sauna and steam room."
    website="https://seascrubsauna.co.uk/"
    map="https://maps.app.goo.gl/TVjpSFNbtTkGkGFD8"
  />
  <ThingsCard
    title="Turner Contemporary"
    image="/things-to-do/turner.jpg"
    description="A striking modern art gallery on the seafront, inspired by J.M.W. Turner and featuring world-class exhibitions."
    website="https://turnercontemporary.org"
    map="https://maps.google.com/?q=Turner+Contemporary+Margate"
  />

      <ThingsCard
    title="Broadstairs Folk Week"
    image="/things-to-do/folk-week.webp"
    description="An annual traditional folk music and dance festival that transforms the Kent seaside town into a 'living stage' for a week each August, and concludes the weekend of our wedding."
    website="https://broadstairsfolkweek.org.uk/"
    map="https://maps.google.com/?q=Broadstairs+Folk+Week"
  />

  <ThingsCard
    title="Dickens House Museum"
    image="/things-to-do/dickens-house.jpeg"
    description="Charles Dickens’ former holiday home, now a museum celebrating his life and connection to Broadstairs."
    website="https://www.visitthanet.co.uk/attractions/dickens-house-museum-1999/"
    map="https://maps.google.com/?q=Dickens+House+Museum+Broadstairs"
  />

  <ThingsCard
  title="Reculver Towers & Roman Fort"
  image="/things-to-do/reculver.jpg"
  description="One of Kent’s most striking coastal landmarks. Originally built in the 3rd century as a Roman fort defending the Saxon Shore, the dramatic twin towers now stand against sweeping sea views and open skies. A beautiful spot for a coastal walk, sunset views, and a little slice of history just a short drive from Ramsgate."
  website="https://www.english-heritage.org.uk/visit/places/reculver-towers-and-roman-fort/"
  map="https://maps.google.com/?q=Reculver+Towers+and+Roman+Fort"
/>
        </div>
        <Divider />
      </Section>
    </>
  );
}

function GalleryPage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const close = () => setLightboxIndex(null);
  const prev = () =>
    setLightboxIndex((i) => (i > 0 ? i - 1 : GALLERY_IMAGES.length - 1));
  const next = () =>
    setLightboxIndex((i) => (i < GALLERY_IMAGES.length - 1 ? i + 1 : 0));

  return (
    <>
      <PageHeader
        title="Gallery"
        imageSrc="/Dancers.webp"
        alt="Gallery artwork"
      />

      <Section className="py-12">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {GALLERY_IMAGES.map((img, index) => (
            <button
  key={index}
  onClick={() => setLightboxIndex(index)}
  className={`overflow-hidden rounded-2xl shadow focus:outline-none ${
  img.fullWidth
    ? "col-span-2 sm:col-span-3"
    : img.orientation === "landscape"
    ? "col-span-2 sm:col-span-1"
    : ""
}`}
>
              <img
  src={img.src}
  alt={img.alt}
  className={`h-full w-full object-cover transition-transform duration-300 hover:scale-105 ${img.mobileFocus || ""}`}
  loading="lazy"
/>
            </button>
          ))}
        </div>
      </Section>

      <Lightbox
        images={GALLERY_IMAGES}
        index={lightboxIndex}
        onClose={close}
        onPrev={prev}
        onNext={next}
      />

      <Divider />

    </>
  );
}

function FAQPage() {
  const FAQS = [
    {
      q: "When should I RSVP by?",
      a: "Please could you respond by 30th of April 2026 so we can get numbers down and make our final preparations.",
    },
    {
      q: "What time should we arrive?",
      a: "The ceremony will start at 2pm so arrival by 1:30pm is a must, and contrary to what you may all think neither the bride or the groom will be late — for once.",
    },
    {
      q: "How long is the ceremony?",
      a: "As quick as possible! — let’s get to the food and drink! We’re aiming for a 20 minute ceremony so please don’t be late as we're not doing it again.",
    },
    {
      q: "Is there parking on site?",
      a: "Yes — there is plenty of parking on-site at Chapel House Estate and if you are staying overnight you may leave your cars there.",
    },
    {
      q: "Is there a dress code?",
      a: "Our main desire is for everyone to feel comfortable in what they are wearing. We’ve gone for smart or smart casual, so you can wear a suit if you wish, or something less formal. The ceremony will be outside in the Orchard, so watch those heels, and remember it’s the British summer which means we could be basking in the sunshine or ducking out of the rain. Either way we’re sure you’ll all look great!",
    },
    {
      q: "What if the weather turns?",
      a: "We all know that no one can predict the British weather so if the skies open there is a lovely indoor venue where the ceremony can take place (but let’s hope we won’t need it).",
    },
    {
      q: "Can I bring someone with me?",
      a: "Due to venue capacity we’re sadly limited on numbers, so if you really do want to bring anyone not on the guest list then talk to us first and we’ll see what we can do.",
    },
    {
      q: "Are children welcome?",
      a: "No! Please, no! — Joking aside, as much as we love your little ones we want to keep our wedding child-free as this allows all the parents to have a good time without having to look after any kids. If this causes any issues regarding your attendance then please let one of us know.",
    },
    {
      q: "What’s for dinner?",
      a: "We’re having BBQ! Yum! There will be a selection of different BBQ food to suit everyone’s taste, and in the evening there’ll be some pizza to keep us all fuelled for more dancing.",
    },
    {
      q: "Will there be a bar?",
      a: "Yes! Of course there will be a bar - this is an Anglo-Irish Wedding! We would like to extend our gratitude to you all for coming by offering beer and wine on us.",
    },
    {
      q: "What time do festivities end?",
      a: "We have a hard midnight curfew. Anything later than that and expect a visit from angry locals brandishing pitch-forks.",
    },
    {
      q: "How can I get home after the event?",
      a: "There are many taxi services in Thanet if you wish to arrange your own transport. We are also going to lay on a midnight bus for those who want it, which will take people back to their accommodation. You will have made your selection when you RSVP’d but if you need to change anything then please let us know by 30th of April so we can get the correct numbers.",
    },
    {
      q: "Should I bring a gift?",
      a: "Your presence at our wedding is the best gift as it’s all about celebration. However, should you wish, to make a contribution to our honeymoon fund — that would be greatly appreciated.",
    },
    {
      q: "Should I bring confetti?",
      a: "No, there is no need. We will supply some environmentally friendly confetti on the day.",
    },
    {
      q: "Is the venue accessible?",
      a: "Yes — the Chapel House Estate venue is wheelchair accessible.",
    },
    {
      q: "Can I take photos or video during the ceremony?",
      a: "We respectfully ask that you refrain from taking photos or video on your phones during the ceremony. As it’s so short, we’d love for you all to be there in the moment. We have an amazing photographer and content creator, both of whom will be capturing all the good stuff. However, once the ceremony is done feel free to snap away — we’ll make a digital photo album online with a QR code, where you can share all your pics.",
    },
    {
      q: "What if I can no longer attend?",
      a: "We understand that life happens and sometimes plans change. If you are no longer able to attend, please let us know as soon as possible so we can update our numbers. We’ll be in touch with you directly if there are any changes to the event.",
    },
    {
      q: "Will there be a Day Two?",
      a: "Despite Alec's protestations that 'Day Two is not a thing'... there will be a Day Two - although a relaxed one. We'll be at The Royal Albion Hotel from 1pm where there will be some light nibbles and drinks. As this is an open pub, please feel free to bring along children, friends, additional partners or pets, everyone is welcome.",
    },
    {
      q: "How can I find more about Lucy Lindsell's art work?",
      a: "Lucy is one of Alec's talented sisters and her stunning artwork can be found on her website and social media pages www.lindsellart.com, @Artoflucylindsell on YouTube,and @lucelindsell on Instagram.",
    },
  ];

  // ...keep the rest of FAQPage below
    return (
    <>
      <PageHeader title="FAQs" imageSrc="bluebell.webp" alt="FAQs artwork" />

      <Section className="py-12">
        <h2
          className="font-serif text-3xl text-center"
          style={{ color: THEME.cornflower }}
        >
          FAQs
        </h2>
        <p className="mt-2 text-center text-sm text-slate-600">
          If anything’s missing, just message us.
        </p>

        <div className="mt-8 max-w-3xl mx-auto space-y-4">
          {FAQS.map((item) => (
            <Card key={item.q} className="p-0 overflow-hidden">
              <details className="group">
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span
                    className="font-serif text-lg"
                    style={{ color: THEME.sage }}
                  >
                    {item.q}
                  </span>

                  <span
                    className="shrink-0 rounded-full border px-3 py-1 text-xs transition group-open:rotate-180"
                    style={{ borderColor: THEME.sage, color: THEME.sage }}
                  >
                    ⌄
                  </span>
                </summary>

                <div className="px-6 pb-6 -mt-1">
                  <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">
                    {item.a}
                  </p>
                </div>
              </details>
            </Card>
          ))}
        </div>

        <Divider />

      </Section>
    </>
  );
}

function Lightbox({ images, index, onClose, onPrev, onNext }) {
  if (index === null) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white text-3xl"
        aria-label="Close"
      >
        ×
      </button>

      {/* Prev */}
      <button
        onClick={onPrev}
        className="absolute left-4 text-white text-4xl px-2"
        aria-label="Previous image"
      >
        ‹
      </button>

      {/* Image */}
      <img
        src={images[index].src}
        alt={images[index].alt}
        className="max-h-[90vh] max-w-[90vw] object-contain"
      />

      {/* Next */}
      <button
        onClick={onNext}
        className="absolute right-4 text-white text-4xl px-2"
        aria-label="Next image"
      >
        ›
      </button>
    </div>
  );
}

/* ================= APP ROUTES ================= */

function AnimatedRoutes() {
  const location = useLocation();
  


  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.2 }}
      >
        <Routes location={location}>
           <Route path="/" element={<HomePage />} />
          <Route path="/details" element={<DetailsPage />} />
          <Route path="/travel" element={<TravelPage />} />
          <Route path="/rsvp" element={<RSVPPage />} />
          <Route path="/rsvp/party" element={<PartyRSVPPage />} />
          <Route path="/things-to-do" element={<ThingsToDoPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <AccessGate>
      <Layout>
        <AnimatedRoutes />
      </Layout>
    </AccessGate>
  );
}


